import { useAppSelector } from "../redux/hooks";
import { useModuleEnabled } from "../redux/selectors/modules";
import { AD_TYPES, GOALS } from "components/core/blocks/AdPlan/constants";

interface TabOption {
  key: string;
  label: string;
  group: string;
}

const AD_DETAILS_TAB_OPTIONS = {
  PRODUCTS: { key: "products", label: "Products", group: "Products" },
  MEDIA_PLAN: { key: "mediaPlan", label: "Media Plan", group: "Configuration" },
  KEYWORDS: { key: "keywords", label: "Keywords", group: "Keywords" },
  TARGETS: { key: "targets", label: "Targets", group: "Targets" },
  NEGATIVE_KEYWORDS: {
    key: "negative-keywords",
    label: "Negative Keywords",
    group: "Negative Keywords",
  },
  SERVING_HOURS: {
    key: "servingHours",
    label: "Serving Hours",
    group: "Configuration",
  },
  NOTIFICATIONS: {
    key: "notifications",
    label: "Notifications",
    group: "Configuration",
  },
  PLACEMENTS: { key: "placements", label: "Placements", group: "Placements" },
  CAMPAIGN_PLACMENTS: { key: "campaignPlacements", label: "Placement Report", group: "Placement Report" },
  AUDIENCE_REPORTS: { key: "audienceReports", label: "Audience Report", group: "Audience Report" },
  AUTO_ADS: { key: "autoAds", label: "Auto Ads", group: "Auto Ads" },
  INSIGHTS: { key: "insights", label: "Insights", group: "Insights" },
  SEASONALITY: {
    key: "seasonality",
    label: "Weekday Parting",
    group: "Configuration",
  },
  SCHEDULED_CHANGES: {
    key: "scheduledChanges",
    label: "Scheduled Updates",
    group: "Configuration",
  },
  SEARCH_TERM_REPORT: {
    key: "searchTermReport",
    label: "Search Term Report",
    group: "Search Term Report",
  },
  SNAPSHOTS: {
    key: "snapshots",
    label: "Snapshots",
    group: "Snapshots",
  },
  DAY_PARTING: {
    key: "day_parting",
    label: "Dayparting",
    group: "Configuration",
  },
  DEBUG: {
    key: "debug",
    label: "Debug",
    group: "Configuration",
  },
  AUDIT_LOG: {
    key: "auditLog",
    label: "Audit Log",
    group: "Configuration",
  },
  CHANGE_LOG: { key: "changeLog", label: "History", group: "Configuration" },
  RULES: { key: "rules", label: "Rules", group: "Configuration" },
  HARVESTING_CONFIGURATION: { key: "harvesting", label: "Harvesting", group: "Configuration" },
  HARVESTING_RULES: {
    key: "harvestingRules",
    label: "Harvesting Rules",
    group: "Configuration",
  },
} satisfies Record<string, TabOption>;

// Util to get applicable tabs for advertisement details based on media plan type and enabled modules
export const useTabsFromAdType = (
  adType: number,
  adGoal: number,
  isSegmented = false,
  isUnmanagedCategory = false,
  managed: string,
  adPlanParams: {
    nonTrellisEnrolledAd?: boolean;
    nonTrellisEnrolledAdAcceptBids?: boolean;
    audienceBidMultiplierTargetAudienceId?: boolean;
  } = {},
) => {
  const hasSnapshotsModule = useModuleEnabled("SNAPSHOTS");
  const hasMediaPlanInsightsModule = useModuleEnabled("MEDIA_PLAN_INSIGHTS");
  const hasNTMHarvestingModule = useModuleEnabled("EXTERNAL_HARVESTING");
  const hasDebugModule = useModuleEnabled("DEBUG");
  const channel = useAppSelector((state) => state.channel.currentChannel);
  let tabsToRender: TabOption[] = [];

  const {
    PRODUCTS,
    MEDIA_PLAN,
    KEYWORDS,
    TARGETS,
    NOTIFICATIONS,
    CAMPAIGN_PLACMENTS,
    AUDIENCE_REPORTS,
    AUTO_ADS,
    SERVING_HOURS,
    INSIGHTS,
    SEASONALITY,
    SCHEDULED_CHANGES,
    SEARCH_TERM_REPORT,
    SNAPSHOTS,
    CHANGE_LOG,
    DAY_PARTING,
    RULES,
    HARVESTING_CONFIGURATION,
    HARVESTING_RULES,
    DEBUG,
    AUDIT_LOG,
  } = AD_DETAILS_TAB_OPTIONS;

  if (adGoal === GOALS.IMPORTED_ADS.value) {
    if (channel === "walmart") {
      if ([AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(adType)) {
        tabsToRender = [PRODUCTS, KEYWORDS, MEDIA_PLAN, NOTIFICATIONS];
      } else {
        tabsToRender = [PRODUCTS, KEYWORDS, AUTO_ADS, MEDIA_PLAN, NOTIFICATIONS];
      }
      return tabsToRender;
    }

    tabsToRender = [PRODUCTS, TARGETS, MEDIA_PLAN, SERVING_HOURS, DAY_PARTING, RULES, SCHEDULED_CHANGES];

    if ([AD_TYPES.SPONSORED_DISPLAY.value, AD_TYPES.SPONSORED_DISPLAY_V2.value].includes(adType)) {
      if (!adPlanParams.nonTrellisEnrolledAd && !adPlanParams.nonTrellisEnrolledAdAcceptBids) {
        tabsToRender.splice(1, 1);
      }
    } else {
      tabsToRender.splice(1, 0, KEYWORDS);
    }
    if (adGoal === GOALS.IMPORTED_ADS.value && hasNTMHarvestingModule && managed === "Yes") {
      tabsToRender.push(HARVESTING_CONFIGURATION);
    }

    if ([AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(adType)) {
      tabsToRender.splice(-1, 0, CAMPAIGN_PLACMENTS);
      tabsToRender.push(HARVESTING_RULES);
    }

    if (
      [
        AD_TYPES.SPONSORED_PRODUCT.value,
        AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
        AD_TYPES.SPONSORED_BRAND.value,
        AD_TYPES.SPONSORED_BRAND_V2.value,
        AD_TYPES.SPONSORED_BRAND_VIDEO.value,
      ].includes(adType)
    ) {
      tabsToRender.splice(-1, 0, SEARCH_TERM_REPORT);
    }

    if (adPlanParams.audienceBidMultiplierTargetAudienceId) {
      tabsToRender.push(AUDIENCE_REPORTS);
    }

    if ([AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_V2.value].includes(adType)) {
      tabsToRender.splice(0, 1);
    }

    if (!isUnmanagedCategory) {
      tabsToRender.push(SEASONALITY);
    }

    if (hasSnapshotsModule) {
      tabsToRender.push(SNAPSHOTS);
    }

    if (hasDebugModule && channel === "amazon") {
      tabsToRender.push(DEBUG);
      tabsToRender.push(AUDIT_LOG);
    }

    return tabsToRender;
  }
  if (channel === "amazon") {
    switch (adType) {
      case AD_TYPES.SPONSORED_PRODUCT.value:
      case AD_TYPES.SPONSORED_PRODUCT_AUTO.value:
        tabsToRender = [PRODUCTS, KEYWORDS, TARGETS, CAMPAIGN_PLACMENTS, MEDIA_PLAN, NOTIFICATIONS, RULES];
        tabsToRender.push(HARVESTING_RULES);
        break;
      case AD_TYPES.CUSTOM.value:
        tabsToRender = [PRODUCTS, MEDIA_PLAN];
        break;
      case AD_TYPES.SPONSORED_BRAND.value:
      case AD_TYPES.SPONSORED_BRAND_V2.value:
      case AD_TYPES.SPONSORED_BRAND_VIDEO.value:
        tabsToRender = [KEYWORDS, TARGETS, MEDIA_PLAN, CAMPAIGN_PLACMENTS, NOTIFICATIONS, RULES];
        break;
      case AD_TYPES.SPONSORED_DISPLAY.value:
      case AD_TYPES.SPONSORED_DISPLAY_V2.value:
        tabsToRender = [PRODUCTS, TARGETS, MEDIA_PLAN, NOTIFICATIONS, RULES];
        break;
      default:
        tabsToRender = [];
        break;
    }
  }

  if (channel === "walmart") {
    if ([AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(adType)) {
      tabsToRender = [PRODUCTS, KEYWORDS, MEDIA_PLAN, NOTIFICATIONS];
    } else {
      tabsToRender = [PRODUCTS, KEYWORDS, AUTO_ADS, MEDIA_PLAN, NOTIFICATIONS];
    }
  }

  if (
    [
      AD_TYPES.SPONSORED_PRODUCT.value,
      AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
      AD_TYPES.SPONSORED_BRAND.value,
      AD_TYPES.SPONSORED_BRAND_V2.value,
      AD_TYPES.SPONSORED_BRAND_VIDEO.value,
      AD_TYPES.SPONSORED_PRODUCT_WALMART.value,
      AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value,
      AD_TYPES.SPONSORED_BRAND_WALMART.value,
      AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
    ].includes(adType) &&
    // Components with parent have number set in segmented flag
    isSegmented !== true
  ) {
    tabsToRender.push(SEARCH_TERM_REPORT);
  }

  if (hasSnapshotsModule) {
    tabsToRender.push(SNAPSHOTS);
  }

  if (hasMediaPlanInsightsModule) {
    tabsToRender.push(INSIGHTS);
  }

  if (channel === "amazon") {
    tabsToRender.push(SEASONALITY);
    tabsToRender.push(DAY_PARTING);
  }

  if (!isSegmented) {
    tabsToRender.push(SCHEDULED_CHANGES);
  }

  if (adPlanParams.audienceBidMultiplierTargetAudienceId) {
    tabsToRender.push(AUDIENCE_REPORTS);
  }

  // Below address shared ad type value between Walmart Sponsored Product and Segmented parent ad values
  if ((adType !== AD_TYPES.CUSTOM.value && adGoal !== GOALS.IMPORTED_ADS.value) || channel === "walmart") {
    tabsToRender.push(CHANGE_LOG);
  }

  if (!(adType === AD_TYPES.CUSTOM.value && channel === "amazon" && adGoal !== GOALS.IMPORTED_ADS.value)) {
    const spliceOffset = hasMediaPlanInsightsModule ? -3 : -2;

    tabsToRender.splice(spliceOffset, 0, SERVING_HOURS);
  }

  if (hasDebugModule && channel === "amazon") {
    tabsToRender.push(DEBUG);
    tabsToRender.push(AUDIT_LOG);
  }

  return tabsToRender;
};
